import { useEffect, useState } from 'react'

import { BudgetedList, SectionCard, ValueOrReason } from '@/components/warehouse/primitives'
import { resolveStorefront, StorefrontMark } from '@/components/warehouse/storefronts'
import { displayPersonName } from '@/lib/order-helpers'
import { getProductOrderHistory } from '@/lib/api/client/inventory'
import type { ProductOrderHistory as OrderHistory, ProductOrderHistoryItem, ProductOrderStatus } from '@/lib/api/schemas/inventory.schema'
import { warehouseDateTime } from '@/lib/warehouse/labels'

export const productOrderStatusLabels: Record<ProductOrderStatus, string> = {
  delivered: 'Доставлен',
  cancelled: 'Отменён',
  in_delivery: 'В доставке',
  processing: 'В обработке',
  unrecognized: 'Статус не распознан',
}

const ORDER_BUDGET = 3

const currencySigns: Record<string, string> = { RUR: '₽', RUB: '₽' }

type HistoryState = { productId: string; history: OrderHistory | null; failed: boolean }

function formatLineAmount({ amount, currency }: ProductOrderHistoryItem['lineAmount']): string {
  return `${amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${currencySigns[currency] ?? currency}`
}

function RecordedText({ value, reason, message }: { value: string | null; reason: string | null; message: string | null }) {
  return <ValueOrReason value={value} reason={reason} message={message} />
}

function OrderLine({ order }: { order: ProductOrderHistoryItem }) {
  const cancelled = order.status === 'cancelled'
  const storefront = resolveStorefront(order.marketplace)
  const storefrontName = storefront.kind === 'known' ? storefront.entry.label : 'Неизвестная площадка'
  return (
    <li
      data-order-status={order.status}
      data-muted={cancelled ? 'true' : undefined}
      className={`rounded-md border px-2.5 py-2 ${cancelled ? 'border-rule/60 bg-bone/40 text-ink-muted' : 'border-rule text-ink-2'}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className={`inline-flex items-center gap-1.5 ${cancelled ? 'font-medium' : 'font-medium text-ink'}`}>
          <StorefrontMark providerCode={order.marketplace} opacity={cancelled ? 0.55 : 1} />
          <span>{storefrontName} · № {order.externalOrderId}</span>
        </span>
        <time dateTime={order.orderedAt} className="text-[10px] text-ink-muted">{warehouseDateTime(order.orderedAt)}</time>
      </div>
      <p className="mt-1">
        <span className={cancelled ? 'font-medium' : 'font-medium text-ink'}>{productOrderStatusLabels[order.status]}</span>
        {' · '}
        <RecordedText value={order.customerName === null ? null : displayPersonName(order.customerName)} reason={order.customerNameUnavailableReason} message={order.customerNameUnavailableMessage} />
        {' · '}
        <RecordedText value={order.city} reason={order.cityUnavailableReason} message={order.cityUnavailableMessage} />
        {' · '}
        <span className={`tabular-nums ${cancelled ? 'line-through' : ''}`}>{formatLineAmount(order.lineAmount)}</span>
      </p>
    </li>
  )
}

/** A piece's orders, each with its own outcome. A piece with no order renders nothing. */
export function ProductOrderHistory({ productId }: { productId: string }) {
  const [state, setState] = useState<HistoryState>({ productId, history: null, failed: false })

  useEffect(() => {
    const controller = new AbortController()
    setState({ productId, history: null, failed: false })
    getProductOrderHistory(productId, controller.signal)
      .then((history) => { if (!controller.signal.aborted) setState({ productId, history, failed: false }) })
      .catch(() => { if (!controller.signal.aborted) setState({ productId, history: null, failed: true }) })
    return () => controller.abort()
  }, [productId])

  // The first render for a new piece must not show the previous piece's orders.
  const current = state.productId === productId ? state : { productId, history: null, failed: false }
  if (current.failed) return <SectionCard title="История заказов"><p role="alert" className="text-xs text-st-unpaid">История заказов недоступна.</p></SectionCard>
  if (current.history === null) return <SectionCard title="История заказов"><p aria-live="polite" className="text-xs text-ink-muted">Загружаем историю заказов…</p></SectionCard>
  if (current.history.total === 0) return null
  return (
    <SectionCard title="История заказов">
      <div data-order-history="true">
        <BudgetedList budget={ORDER_BUDGET} total={current.history.total} noun="заказов" className="space-y-1.5 text-xs">
          {current.history.items.map((order) => (
            <OrderLine key={`${order.marketplace}:${order.externalOrderId}:${order.orderedAt}`} order={order} />
          ))}
        </BudgetedList>
      </div>
    </SectionCard>
  )
}
